import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  IconButton,
  Box,
  Typography,
  Tooltip,
  Modal,
  Grid,
  Card,
  CardContent,
  Button,
  Input,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import FileSaveIcon from '../../../../../assets/svg/file_save.svg?react';
import ContentCopyIcon from '../../../../../assets/svg/content_copy.svg?react';
import styles from './LibraryList.module.scss';
import { v4 as uuidv4 } from 'uuid';
import { LibraryListProps, LibraryItem } from './types';
import { useAppSelector } from '../../../../common/hooks/useAppSelector';
import { selectAllDocuments } from '../../../../slices/documents/documentsSlice';
import { selectAllCompanies } from '../../../../slices/companies/companiesSlice';
import { useLibraryFunctions } from '../../hooks/useLibraryFunctions';
import MessageModal from '../../../../common/message-modal/MessageModal';
import { ROUTES } from '../../../../common/constants/routes';
import { useNavigate } from 'react-router';
import FileTypeDownloadModal from '../../../../common/file-type-download-modal/FileTypeDownloadModal';
import { useFileTypeDownload } from '../../../../common/file-type-download-modal/useFileTypeDownload';
import { ProgressOverlay } from '../../../../common/progress-overlay/ProgressOverlay';
import MenuBlock from '../../../../common/menu-block';
import VerticalDotsIcon from '../../../../../assets/svg/more_vert.svg?react';
import { useDeleteLibraryItemMessageModal } from '../../hooks/useDeleteMessageModal';
import ContractGeneratorIcon from '../../../../../assets/svg/contract-generator-icon.svg?react';
import {
  selectDisclaimerChecked,
  acceptDisclaimer,
} from '../../../../slices/disclaimer/disclaimerSlice';
import { useAppDispatch } from '../../../../common/hooks/useAppDispatch';
import DisclaimerText from '../../../../common/disclaimer-text/DisclaimerText';
import { LibraryListRow } from './LibraryListRow';
import { selectUser } from '../../../../slices/user/userSlice';
import { showMessage } from '../../../../slices/show-plan-upgrading-message/showPlanUpgradingMessage';
import {
  showOverlay,
  hideOverlay,
} from '../../../../slices/show-progress-overlay/showProgressOverlay';
import { Helpers } from '../../../../common/helpers/helpers';
import { NWClient } from '../../../../client/NWClient';
import { toast } from 'react-toastify';
import { useErrorMessage } from '../../../../common/hooks/useErrorMessage';

type CopyModalProps = {
  item: LibraryItem;
  open: boolean;
  category?: string;
  currentUserId: number;
  onClose: () => void;
  onLibraryItemCopy: (data: unknown, id: number) => void;
  onTemplateCopy: (data: unknown, id: number, category?: string) => void;
};

const CopyModal = ({
  item,
  open,
  category,
  currentUserId,
  onClose,
  onLibraryItemCopy,
  onTemplateCopy,
}: CopyModalProps) => {
  const { t } = useTranslation();
  // Stamped once when the modal mounts, so the clock is not read during render.
  const [defaultTemplateName] = useState(() => {
    const stampedAt = new Date();
    const date = stampedAt.toLocaleDateString();
    const time = stampedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const baseName = item.name ? item.name : t('templatePage.headerDefault');
    return `${baseName} - ${date} ${time}`;
  });
  const handleCopying = (newname: string) => {
    const data = {
      name: newname,
      category: item.category,
      dt_workflows: item.dl_workflows,
      dt_types: item.dl_types,
      user: currentUserId,
    };

    try {
      onLibraryItemCopy(data, item.id); // Wait for the copy operation to complete
      onClose(); // Close the modal after the operation finishes
    } catch (error) {
      console.error('Error copying library item:', error);
    }
  };
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const newTemplateName = formData.get('newTemplateName') as string;
    if ('region' in item) {
      handleCopying(newTemplateName);
    } else {
      const updatedItem = { ...item, name: newTemplateName };
      try {
        onTemplateCopy(
          updatedItem,
          currentUserId,
          category === 'All' ? undefined : updatedItem.category
        );
        onClose();
      } catch (error) {
        console.error('Error copying template:', error);
      }
    }
  };
  return (
    <Modal open={open} className={styles.copymodal} onClose={onClose}>
      <Grid component='div' container sx={{ boxShadow: 'none', border: 'none', outline: 'none' }}>
        <Grid component='div' size={{ xs: 12, sm: 11, md: 11, lg: 11 }}>
          <Card>
            <CardContent sx={{ paddingBottom: '16px !important' }}>
              <Typography variant={'h6'} gutterBottom>
                {t('headers.copyToMyTemplates')}
              </Typography>

              <Typography>Please change the template name if necessary.</Typography>
              <form onSubmit={handleSubmit}>
                <Box sx={{ maxHeight: '500px', overflow: 'auto' }}>
                  <Input
                    name='newTemplateName'
                    placeholder={item.name ? item.name : t('templatePage.headerDefault')}
                    defaultValue={defaultTemplateName}
                    className={styles.input}
                    fullWidth
                    required
                  />
                </Box>

                <Grid
                  component='div'
                  container
                  style={{
                    justifyContent: 'flex-end',
                    gap: '8px',
                    flexWrap: 'wrap',
                    marginTop: '16px',
                  }}
                >
                  <Button
                    id='libraryListGoBackBtn'
                    variant='contained'
                    onClick={() => onClose()}
                    className={'btn-default btn-action-basic btn-cancel'}
                  >
                    Go back
                  </Button>

                  <Button
                    id='libraryListConfirmBtn'
                    variant='contained'
                    type='submit'
                    className='btn-default btn-action-basic'
                  >
                    Confirm
                  </Button>
                </Grid>
              </form>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Modal>
  );
};

const LibraryList = ({
  libraryItems,
  cardView,
  ownTemplate,
  category,
  embedded,
}: LibraryListProps) => {
  const { t } = useTranslation();
  const { getErrorMessage } = useErrorMessage();
  const dispatch = useAppDispatch();
  const [copyModalOpen, setCopyModalOpen] = useState<LibraryItem | null>(null);
  const company = useAppSelector(selectAllCompanies)[0];
  const navigate = useNavigate();
  const documentUploads = useAppSelector(selectAllDocuments);
  const disclaimerAccepted = useAppSelector(selectDisclaimerChecked);
  const disclaimerStatus = useAppSelector((state) => state.disclaimerInfo.status);
  const freePlan =
    (!company?.stripe_product_id && !company?.plan_settings.stripe_product_id) ||
    company?.stripe_product_name?.includes('eSign');
  const currentUser = useAppSelector(selectUser);
  const {
    handleS3DownloadLink,
    handleLibraryItemCopy,
    planUpgradingMessage,
    setPlanUpgradingMessage,
    navigateToTemplatePage,
    disclaimerMessage,
    handleTemplateCopy,
    setDisclaimerMessage,
    libraryItemForGenerator,
    setLibraryItemForGenerator,
  } = useLibraryFunctions();
  const {
    setModalOpen: setFileTypeModalOpen,
    modalOpen: fileTypeModalOpen,
    handleCloseModal: handleCloseFileTypeModal,
    libraryItem: libraryItemForDownloading,
    setLibraryItem: setLibraryItemForDownloading,
    setFileName: setFileNameForDownloading,
    fileName: fileNameForDownloading,
    fileType: fileTypeForDownloading,
    setFileType: setFileTypeForDownloading,
    handleTemplateDownload,
  } = useFileTypeDownload();
  const [pdfSaveDialogOpen, setPdfSaveDialogOpen] = useState(false);

  const {
    modalOpen: deleteLibraryItemModalOpen,
    setModalOpen: setDeleteLibraryItemModalOpen,
    handleDeleteDocumentTemplate,
    libraryItemToDelete,
    setLibraryItemToDelete,
    pendingStatus: deletingLibraryItemPending,
  } = useDeleteLibraryItemMessageModal();

  const handleNavigate = (item: LibraryItem, editMode?: boolean) => {
    const actionAvailableFree = 'is_free' in item && item.is_free;
    if (item.download_link) {
      return false;
    } else {
      if (freePlan) {
        if (actionAvailableFree || !item.region) {
          navigateToTemplatePage(item.region ?? 'company-templates', item.id, editMode);
        } else {
          dispatch(showMessage(t('messages.considerStartingPlanLibrary')));
        }
      } else {
        navigateToTemplatePage(item.region ?? 'company-templates', item.id, editMode);
      }
    }
  };

  const isEmploymentCategory = category?.toLowerCase() === 'employment' && !ownTemplate;

  // The "Filter by Requirement" control lives in the Library page aside (see Library.tsx); the
  // table only shows the Requirement column.

  // The list follows the Library mockup: no header row. Each row carries the template name with
  // its document type and date underneath, then the Requirement badge (employment only) and the
  // action buttons on the right.

  const renderActionButtons = (item: LibraryItem) => {
    const actionAvailableFree = 'is_free' in item && item.is_free;
    const templateMoreActions = [
      {
        name: t('buttons.view'),
        onClick: () => {
          handleNavigate(item);
        },
      },
      {
        name: t('buttons.edit'),
        onClick: () => {
          handleNavigate(item, true);
        },
      },
      {
        name: t('buttons.delete'),
        onClick: () => {
          setLibraryItemToDelete(item);
          setDeleteLibraryItemModalOpen(true);
        },
      },
    ];

    const handleGenerateContract = () => {
      const actionAvailableFree = 'is_free' in item && item.is_free;
      if ((freePlan && actionAvailableFree) || !freePlan || !('region' in item)) {
        if (disclaimerAccepted) {
          navigate(
            `${ROUTES.DOCUMENT_LIBRARY}/${item.region ? item.region : 'company-templates'}/${
              item.id
            }?generateDocument=true`
          );
        } else {
          setLibraryItemForGenerator(item);
          setDisclaimerMessage(true);
        }
      } else if (freePlan && !actionAvailableFree) {
        dispatch(showMessage(t('messages.considerStartingPlanLibrary')));
      }
    };

    const handleDownloading = () => {
      if (!item.download_link) {
        setFileNameForDownloading(
          item.name ??
            documentUploads
              ?.find((el) => el.id === item.document_upload)
              ?.name?.replace('.pdf', '')
              .replace('.docx', '')
              .replace('.doc', '') ??
            ''
        );
        setLibraryItemForDownloading(item);
        setFileTypeModalOpen(true);
      } else {
        handleS3DownloadLink(item.id, 'name' in item ? item.name : '');
      }
    };

    return (
      <Box className={styles.actions_container}>
        {!item.download_link && (
          <Tooltip title={t('buttons.generateContract')} placement='top' arrow>
            <IconButton
              id='libraryListGenerateContractIconBtn'
              size='small'
              onClick={() => handleGenerateContract()}
              className={styles.action_button}
            >
              <ContractGeneratorIcon id='libraryListGenerateContractIconBtnContractGeneratorIcon' />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={t('buttons.download')} placement='top' arrow>
          <IconButton
            id='libraryListDownloadIconBtn'
            size='small'
            onClick={() => {
              if (freePlan || !item.region) {
                if (actionAvailableFree || !item.region) {
                  handleDownloading();
                } else {
                  dispatch(showMessage(t('messages.considerStartingPlanLibrary')));
                }
              } else {
                handleDownloading();
              }
            }}
            className={styles.action_button}
          >
            <FileSaveIcon id='libraryListDownloadIconBtnFileSaveIcon' />
          </IconButton>
        </Tooltip>
        {!item.download_link && (
          <Tooltip
            title={'content' in item ? t('buttons.duplicateTemplate') : t('buttons.copyTemplate')}
            placement='top'
            arrow
          >
            <IconButton
              id='libraryListCopyTemplateIconBtn'
              size='small'
              onClick={() => {
                if (freePlan || !item.region) {
                  if (actionAvailableFree || !item.region) {
                    setCopyModalOpen(item);
                  } else {
                    dispatch(showMessage(t('messages.considerStartingPlanLibrary')));
                  }
                } else {
                  setCopyModalOpen(item);
                }
              }}
              className={styles.action_button}
            >
              <ContentCopyIcon id='libraryListCopyTemplateIconBtnContentCopyIcon' />
            </IconButton>
          </Tooltip>
        )}
        {'document_upload' in item && (
          <MenuBlock
            id='libraryListVerticalDotsMenu'
            options={templateMoreActions}
            moreIcon={<VerticalDotsIcon id='libraryListVerticalDotsMenuVerticalDotsIcon' />}
          />
        )}
      </Box>
    );
  };

  return (
    <>
      <TableContainer
        component={Paper}
        className={`${styles.table_container}${
          embedded ? ` ${styles.table_container_embedded}` : ''
        }`}
        sx={{
          boxShadow: 'none',
          // The embedded table takes its max height from `.table_container_embedded`
          // (desktop only: on tablet / mobile the aside sits above it and the page scrolls).
          ...(embedded ? {} : { maxHeight: `calc(100vh - ${cardView ? 219 : 179}px)` }),
          overflow: 'auto',
        }}
      >
        <Table className={styles.library_table} aria-label={t('libraryTable.name')}>
          <TableBody>
            {libraryItems.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <p className={styles.empty_message}>
                    {t('emptyStateMessages.emptyLibraryMessage')}
                  </p>
                </TableCell>
              </TableRow>
            )}
            {libraryItems.map((item) => {
              return (
                <React.Fragment key={`${item.id}-${uuidv4()}`}>
                  <LibraryListRow
                    item={item}
                    renderActionButtons={renderActionButtons}
                    showRequirement={isEmploymentCategory}
                    ownTemplate={ownTemplate}
                  />
                  <CopyModal
                    item={item}
                    open={copyModalOpen?.id === item.id}
                    category={category}
                    currentUserId={currentUser.id}
                    onClose={() => setCopyModalOpen(null)}
                    onLibraryItemCopy={handleLibraryItemCopy}
                    onTemplateCopy={handleTemplateCopy}
                  />
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {planUpgradingMessage && (
        <MessageModal
          open={planUpgradingMessage}
          modalMessage={t('messages.considerStartingPlanLibrary')}
          actionButtonText={t('buttons.ok')}
          actionButtonFn={() => navigate(ROUTES.PRICING)}
          goBackButtonText={t('buttons.later')}
          onClose={() => {
            setPlanUpgradingMessage(false);
          }}
        />
      )}
      {fileTypeModalOpen && libraryItemForDownloading && (
        <FileTypeDownloadModal
          onClose={() => {
            handleCloseFileTypeModal();
          }}
          open={fileTypeModalOpen}
          fileName={fileNameForDownloading}
          fileType={fileTypeForDownloading}
          handleFileTypeChange={(value) => setFileTypeForDownloading(value as 'pdf' | 'doc')}
          handleFileNameChange={setFileNameForDownloading}
          libraryItem={libraryItemForDownloading}
          handleDownloadLibraryHTML={() => {
            if (fileTypeForDownloading === 'pdf') {
              setPdfSaveDialogOpen(true);
              setFileTypeModalOpen(false);
            } else {
              handleTemplateDownload(
                libraryItemForDownloading.id,
                fileTypeForDownloading,
                fileNameForDownloading,
                ownTemplate
              );
            }
          }}
        />
      )}
      {pdfSaveDialogOpen && libraryItemForDownloading && (
        <MessageModal
          open={pdfSaveDialogOpen}
          modalMessage={t('messages.chooseSaveAsPDF')}
          actionButtonText={t('buttons.ok')}
          actionButtonFn={() => {
            if (libraryItemForDownloading && 'content' in libraryItemForDownloading) {
              Helpers.convertHtmlToPdfAndDownload(
                libraryItemForDownloading?.content as string,
                fileNameForDownloading
              );
              setPdfSaveDialogOpen(false);
              setLibraryItemForDownloading(null as unknown as LibraryItem);
            } else {
              dispatch(showOverlay());
              NWClient.get('document-library-html', libraryItemForDownloading?.id)
                .then((res) => {
                  const htmlRes = res as { id: number; html: string };
                  Helpers.convertHtmlToPdfAndDownload(htmlRes.html, fileNameForDownloading);
                  setPdfSaveDialogOpen(false);
                  setLibraryItemForDownloading(null as unknown as LibraryItem);
                })
                .catch((error) => {
                  toast.error(getErrorMessage(error));
                })
                .finally(() => {
                  dispatch(hideOverlay());
                  setPdfSaveDialogOpen(false);
                });
            }
          }}
          goBackButtonText={t('buttons.goBack')}
          onClose={() => {
            setPdfSaveDialogOpen(false);
          }}
        />
      )}
      {deleteLibraryItemModalOpen && libraryItemToDelete && (
        <MessageModal
          open={deleteLibraryItemModalOpen}
          modalTitle={t('dialogTitles.deleteTemplate')}
          modalMessage={t('messages.deleteTemplate')}
          actionButtonText={t('buttons.goBack')}
          actionButtonFn={() => {
            setLibraryItemToDelete(null);
            setDeleteLibraryItemModalOpen(false);
          }}
          goBackButtonText={t('buttons.delete')}
          onDismiss={() => {
            setLibraryItemToDelete(null);
            setDeleteLibraryItemModalOpen(false);
          }}
          onClose={() => {
            handleDeleteDocumentTemplate();
          }}
        />
      )}
      {disclaimerMessage && (
        <MessageModal
          open={disclaimerMessage}
          modalTitle={t('headers.documentTerms')}
          modalContent={<DisclaimerText />}
          actionButtonText={t('buttons.accept')}
          headerVariant={'h5'}
          actionButtonFn={() => {
            dispatch(acceptDisclaimer())
              .unwrap()
              .then(() => {
                setDisclaimerMessage(false);
                navigate(
                  `${ROUTES.DOCUMENT_LIBRARY}/${
                    libraryItemForGenerator?.region
                      ? libraryItemForGenerator?.region
                      : 'company-templates'
                  }/${libraryItemForGenerator?.id}?generateDocument=true`
                );
                //setGeneratorOpened(true);
              });
          }}
          goBackButtonText={t('buttons.goBack')}
          onClose={() => {
            setDisclaimerMessage(false);
            setLibraryItemForGenerator(null as unknown as LibraryItem);
          }}
        />
      )}
      {(deletingLibraryItemPending || disclaimerStatus === 'loadingAccepting') && (
        <ProgressOverlay />
      )}
    </>
  );
};

export default LibraryList;
